import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, Check } from "lucide-react";
import { findUseCase, FUNC_META, FUNC_GROUPS, casesForFunc } from "@/data/useCases";
import { USE_CASE_BULLETS } from "@/data/useCaseBullets";
import { FUNC_ICONS } from "@/components/LineIcons";
import { useBookCall } from "@/components/BookCallContext";
import CSATGauge from "@/components/CSATGauge";
import SEO from "@/components/SEO";
import { TESTIDS } from "@/constants/testIds";

/**
 * Use-case detail page (aligns with the user-supplied reference exactly):
 *   1. Slim function-group switcher (pills)
 *   2. "Agents in this group" horizontal card band, active agent glows
 *   3. "Back to <group>" breadcrumb link
 *   4. Kicker · big H1 title · one-liner subtitle
 *   5. Two-column split:
 *        LEFT , Problem paragraph, How we solve it paragraph,
 *                checkmark bullet list (3 items derived from the solution),
 *                Book a Demo CTA
 *        RIGHT, Rich agent illustration (group image with overlay,
 *                or CSATGauge for the CSAT agent)
 */
export default function UseCaseDetail() {
    const { id } = useParams();
    const u = findUseCase(id);
    const { openModal } = useBookCall();
    const navigate = useNavigate();

    if (!u) return <Navigate to="/use-cases" replace />;
    const meta = FUNC_META[u.func] || FUNC_META.support;
    const accent = meta.accent;
    const groupAgents = casesForFunc(u.func);
    const bullets = deriveBullets(u);

    return (
        <div data-testid={TESTIDS.ucDetail.root}>
            <SEO
                path={`/use-cases/${u.id}`}
                title={`${u.name}, ${meta.name} AI agent | KyroLabs`}
                description={`${u.one} ${u.p} ${u.s}`.slice(0, 300)}
            />
            {/* ===== 1. FUNCTION-GROUP SWITCHER ===== */}
            <div style={switcherBar}>
                <div className="wrap" style={switcherWrap}>
                    <div
                        className="siblings-row"
                        style={{
                            display: "flex",
                            gap: 8,
                            overflowX: "auto",
                            flex: 1,
                            minWidth: 0,
                            scrollbarWidth: "none",
                        }}
                    >
                        {FUNC_GROUPS.map((g) => {
                            const on = g.id === u.func;
                            const col = FUNC_META[g.id].accent;
                            return (
                                <Link
                                    key={g.id}
                                    to={`/use-cases/${g.id}`}
                                    data-testid={TESTIDS.uclist.funcTab(g.id)}
                                    style={pillStyle(on, col)}
                                >
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            color: on ? col : "var(--kl-muted)",
                                        }}
                                        className="switcher-icon"
                                    >
                                        {FUNC_ICONS[g.id]}
                                    </span>
                                    <span
                                        style={{
                                            whiteSpace: "nowrap",
                                            fontSize: ".82rem",
                                        }}
                                    >
                                        {g.name}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ===== 2. AGENTS IN THIS GROUP, slim chip row ===== */}
            <section style={{ padding: "14px 0 8px", borderBottom: "1px solid var(--line)" }}>
                <div className="wrap" style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <span
                        className="font-mono-label"
                        style={{
                            color: "var(--dim)",
                            whiteSpace: "nowrap",
                            fontSize: ".66rem",
                            letterSpacing: ".14em",
                        }}
                    >
                        {meta.name.toUpperCase()} AGENTS
                    </span>
                    <div className="chip-row" style={chipRow}>
                        {groupAgents.map((a) => {
                            const on = a.id === u.id;
                            return (
                                <button
                                    key={a.id}
                                    onClick={() => navigate(`/use-cases/${a.id}`)}
                                    style={chipStyle(on, accent)}
                                    title={a.name}
                                >
                                    <span style={chipDot(on, accent)} />
                                    <span
                                        style={{
                                            whiteSpace: "nowrap",
                                            fontSize: ".78rem",
                                            fontWeight: on ? 600 : 500,
                                        }}
                                    >
                                        {a.name}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ===== 3+4. BREADCRUMB + HERO HEADER (compact) ===== */}
            <section style={{ padding: "22px 0 14px" }}>
                <div className="wrap">
                    <div style={{ marginBottom: 12 }}>
                        <Link to={`/use-cases/${u.func}`} style={backLink}>
                            <ChevronLeft size={14} />
                            Back to {meta.name}
                        </Link>
                    </div>
                    <span
                        className="kicker"
                        style={{ color: accent }}
                    >
                        {meta.name}
                    </span>
                    <h1 className="h1" style={{ margin: "14px 0 12px" }}>
                        {u.name}
                    </h1>
                    <p
                        style={{
                            color: "var(--kl-muted)",
                            fontSize: "1.1rem",
                            lineHeight: 1.6,
                            maxWidth: 720,
                        }}
                    >
                        {u.one}
                    </p>
                </div>
            </section>

            {/* ===== 5. TWO-COLUMN SPLIT (text left, illustration right) ===== */}
            <section style={{ padding: "20px 0 100px" }}>
                <div className="wrap">
                    <div className="ucd-split" style={splitGrid}>
                        {/* LEFT, Problem, Solution, bullets, CTA */}
                        <div>
                            <motion.div
                                data-testid={TESTIDS.ucDetail.problem}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4 }}
                                style={{ marginBottom: 28 }}
                            >
                                <h3
                                    className="font-display"
                                    style={{
                                        fontSize: "1.35rem",
                                        fontWeight: 600,
                                        marginBottom: 10,
                                        color: "var(--ink)",
                                    }}
                                >
                                    Problem
                                </h3>
                                <p
                                    style={{
                                        color: "var(--kl-muted)",
                                        fontSize: "1rem",
                                        lineHeight: 1.65,
                                    }}
                                >
                                    {u.p}
                                </p>
                            </motion.div>

                            <motion.div
                                data-testid={TESTIDS.ucDetail.solution}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: 0.05 }}
                                style={{ marginBottom: 28 }}
                            >
                                <h3
                                    className="font-display"
                                    style={{
                                        fontSize: "1.35rem",
                                        fontWeight: 600,
                                        marginBottom: 10,
                                        color: "var(--ink)",
                                    }}
                                >
                                    How we solve it
                                </h3>
                                <p
                                    style={{
                                        color: "var(--kl-muted)",
                                        fontSize: "1rem",
                                        lineHeight: 1.65,
                                    }}
                                >
                                    {u.s}
                                </p>
                            </motion.div>

                            {bullets.length > 0 && (
                                <ul
                                    style={{
                                        listStyle: "none",
                                        margin: "0 0 32px",
                                        padding: 0,
                                        display: "grid",
                                        gap: 12,
                                    }}
                                >
                                    {bullets.map((b, i) => (
                                        <motion.li
                                            key={i}
                                            initial={{ opacity: 0, x: -8 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{
                                                duration: 0.35,
                                                delay: 0.1 + i * 0.06,
                                            }}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 12,
                                            }}
                                        >
                                            <span style={bulletIcon(accent)}>
                                                <Check size={13} strokeWidth={2.5} />
                                            </span>
                                            <span
                                                style={{
                                                    color: "var(--ink)",
                                                    fontSize: ".98rem",
                                                }}
                                            >
                                                {b}
                                            </span>
                                        </motion.li>
                                    ))}
                                </ul>
                            )}

                            <button
                                data-testid={TESTIDS.ucDetail.bookCall}
                                className="btn btn-p"
                                onClick={() =>
                                    openModal({ source: "use-case", use_case_id: u.id })
                                }
                            >
                                Book a Demo <ArrowRight size={14} />
                            </button>
                        </div>

                        {/* RIGHT, Rich illustration */}
                        <div className="ucd-visual" style={visualColumn}>
                            <UCVisual uc={u} accent={accent} groupImage={meta.image} />
                        </div>
                    </div>
                </div>

                <style>{`
                    .siblings-row::-webkit-scrollbar { display: none; }
                    .chip-row::-webkit-scrollbar { display: none; }
                    .switcher-icon svg { width: 16px; height: 16px; }
                    @media (max-width: 960px) {
                        .ucd-split { grid-template-columns: 1fr !important; gap: 32px !important; }
                    }
                `}</style>
            </section>
        </div>
    );
}

/* ---------- Visual (right column) ---------- */

function UCVisual({ uc, accent, groupImage }) {
    // CSAT special case, big satisfaction gauge inside the illustration frame.
    if (uc.id === "csat") {
        return (
            <div style={visualFrame(accent)}>
                <div style={csatBox(accent)}>
                    <CSATGauge value={92} accent={accent} size={280} />
                    <span
                        className="font-mono-label"
                        style={{
                            color: accent,
                            fontSize: ".72rem",
                            letterSpacing: ".14em",
                            marginTop: 18,
                        }}
                    >
                        SATISFACTION SCORE
                    </span>
                </div>
            </div>
        );
    }
    // Prefer per-agent illustration, fall back to group hero.
    const agentImg = `/assets/images/uc_${uc.id}.webp`;
    return (
        <div style={visualFrame(accent)}>
            <picture>
                <source type="image/webp" srcSet={agentImg} />
                <img
                    src={agentImg}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                        // Fall back to the group hero if the per-agent asset is missing.
                        if (e.currentTarget.src !== groupImage) {
                            e.currentTarget.src = groupImage;
                        }
                    }}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                    }}
                />
            </picture>
            <div style={visualOverlay(accent)}>
                <span
                    className="font-mono-label"
                    style={{
                        color: accent,
                        fontSize: ".7rem",
                        letterSpacing: ".14em",
                    }}
                >
                    KYROLABS
                </span>
                <div
                    className="font-display"
                    style={{
                        fontSize: "1.25rem",
                        fontWeight: 600,
                        color: "#fff",
                        marginTop: 6,
                    }}
                >
                    {uc.name}
                </div>
            </div>
        </div>
    );
}

/* ---------- Bullet derivation ----------
 * Break the solution text on commas / em-dashes / semicolons and clean up
 * the fragments into short capability phrases. Falls back to 0 bullets if
 * we can't produce at least 2 clean ones (the block is then hidden).
 */
function deriveBullets(u) {
    // Explicit hand-curated bullets (preferred).
    const explicit = USE_CASE_BULLETS[u.id];
    if (Array.isArray(explicit) && explicit.length > 0) return explicit;
    if (Array.isArray(u.bullets) && u.bullets.length > 0) return u.bullets;
    const src = u.s || "";
    // Split on commas / em-dashes / semicolons that separate list-like items.
    let parts = src
        .replace(/\.$/, "")
        .split(/[,;,–]|(?:\s,\s)|(?:\s-\s)/)
        .map((p) => p.trim())
        .filter(Boolean);
    // Strip common lead-ins so we get pure capability phrases.
    const leadRE = /^(the agent|it|the )/i;
    parts = parts.map((p) => p.replace(leadRE, "").trim());
    // Keep only reasonably short phrases (2–8 words).
    parts = parts.filter((p) => {
        const w = p.split(/\s+/).length;
        return w >= 2 && w <= 10;
    });
    // Sentence-case + de-dupe.
    const seen = new Set();
    const clean = [];
    for (const p of parts) {
        const c = p.charAt(0).toUpperCase() + p.slice(1);
        if (!seen.has(c.toLowerCase())) {
            seen.add(c.toLowerCase());
            clean.push(c);
        }
        if (clean.length === 3) break;
    }
    return clean.length >= 2 ? clean : [];
}

/* ---------- styles ---------- */

const switcherBar = {
    borderBottom: "1px solid var(--line)",
    background: "rgba(255,255,255,0.015)",
    padding: "10px 0",
};
const switcherWrap = { display: "flex", alignItems: "center", gap: 16 };
const pillStyle = (on, col) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 12px",
    borderRadius: 999,
    border: on ? `1px solid ${col}` : "1px solid var(--line)",
    background: on ? `${col}15` : "transparent",
    color: on ? "var(--ink)" : "var(--kl-muted)",
    textDecoration: "none",
    fontWeight: 500,
    transition: "all .16s",
    boxShadow: on ? `0 0 0 3px ${col}18` : "none",
    flexShrink: 0,
});

// Slim chip row for the "Agents in this group" band, horizontally
// scrollable, single-line, no wrap. Keeps the whole hero area compact so
// the split content is above the fold on desktop.
const chipRow = {
    display: "flex",
    gap: 8,
    overflowX: "auto",
    flex: 1,
    minWidth: 0,
    scrollbarWidth: "none",
    padding: "2px 0",
};
const chipStyle = (on, accent) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 999,
    border: on ? `1px solid ${accent}` : "1px solid var(--line)",
    background: on ? `${accent}15` : "rgba(255,255,255,0.02)",
    color: on ? "var(--ink)" : "var(--kl-muted)",
    cursor: "pointer",
    font: "inherit",
    transition: "all .16s",
    boxShadow: on ? `0 0 0 3px ${accent}22` : "none",
    flexShrink: 0,
});
const chipDot = (on, accent) => ({
    width: 8,
    height: 8,
    borderRadius: 999,
    background: on ? accent : "var(--kl-muted)",
    flexShrink: 0,
    boxShadow: on ? `0 0 8px ${accent}` : "none",
});

const backLink = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: "var(--kl-muted)",
    textDecoration: "none",
    fontSize: ".84rem",
};

// Two-column split: LEFT text ~ 45%, RIGHT illustration ~ 55%.
const splitGrid = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 460px) 1fr",
    gap: 56,
    alignItems: "start",
};

const visualColumn = {
    position: "sticky",
    top: 96,
};

const visualFrame = (accent) => ({
    position: "relative",
    aspectRatio: "5 / 4",
    borderRadius: 20,
    overflow: "hidden",
    border: "1px solid var(--line)",
    background: `radial-gradient(80% 60% at 30% 20%, ${accent}18, transparent 60%),#0a0d12`,
    boxShadow: `0 30px 80px ${accent}18, inset 0 0 0 1px rgba(255,255,255,0.02)`,
});
const visualOverlay = (accent) => ({
    position: "absolute",
    inset: 0,
    padding: 24,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    background:
        `linear-gradient(180deg, rgba(8,9,12,0) 35%, rgba(8,9,12,0.85) 100%)`,
    pointerEvents: "none",
    // subtle accent border-glow on the bottom edge
    boxShadow: `inset 0 -1px 0 ${accent}30`,
});
const csatBox = (accent) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    background: `radial-gradient(55% 55% at 50% 40%, ${accent}22, transparent 65%)`,
});

const bulletIcon = (accent) => ({
    display: "inline-grid",
    placeItems: "center",
    width: 22,
    height: 22,
    borderRadius: 999,
    border: `1.5px solid ${accent}`,
    color: accent,
    flexShrink: 0,
    background: `${accent}12`,
});
